import { PocketRow, PocketSection } from "@/components/ui";
import { Icons } from "@/components/ui/icons";
import { translate, useT } from "@/i18n";
import { framesGithubMessages } from "@/i18n/messages/frames-github";
import { safeWebUrl } from "@/lib/safe-web-url";
import { openExternalUrl } from "@/lib/open-external-url";

export type PullRequest = {
  number: number;
  title: string;
  status: string;
  branch?: string;
  base?: string;
  checks_passed: number;
  checks_total: number;
  checks_failed?: number;
  checks_running?: number;
  checks_status?: "none" | "success" | "failure" | "pending";
  comments: number;
  time?: string;
  url: string;
};

function checksSummary(pr: PullRequest): string | undefined {
  if (!pr.checks_total) return undefined;
  const total = pr.checks_total;
  if (pr.checks_failed)
    return translate(framesGithubMessages, "checksFailedCount", {
      failed: pr.checks_failed,
      total,
    });
  if (pr.checks_running)
    return translate(framesGithubMessages, "checksRunningCount", {
      running: pr.checks_running,
      total,
    });
  return translate(framesGithubMessages, "checksPassedCount", {
    passed: pr.checks_passed,
    total,
  });
}

/** `linked`: rows open the pull request; off in card previews, which are pictures. */
export function PrSection({
  prs,
  linked = true,
}: {
  prs: PullRequest[];
  linked?: boolean;
}) {
  const t = useT(framesGithubMessages);
  if (!prs.length) return null;
  return (
    <PocketSection
      title={t("pullRequests", { count: prs.length })}
      testId="github-pr"
    >
      {prs.map((pr) => {
        const url = linked ? safeWebUrl(pr.url) : null;
        const tone =
          pr.checks_status === "failure" || (pr.checks_failed ?? 0) > 0
            ? "error"
            : pr.checks_status === "pending" || (pr.checks_running ?? 0) > 0
              ? "working"
              : undefined;
        return (
          <PocketRow
            key={pr.number}
            testId={`github-pr-link-${pr.number}`}
            title={pr.title}
            leading={
              <Icons.GitPullRequest className="h-4 w-4" aria-hidden="true" />
            }
            secondLine={[
              pr.base
                ? t("intoBase", { number: pr.number, base: pr.base })
                : `#${pr.number}`,
              checksSummary(pr),
            ]
              .filter(Boolean)
              .join(", ")}
            status={
              tone
                ? {
                    tone,
                    text:
                      tone === "error" ? t("checksFailed") : t("checksRunning"),
                  }
                : undefined
            }
            onSelect={
              url
                ? () => {
                    void openExternalUrl(url);
                  }
                : undefined
            }
            hoverActions={
              url ? (
                <Icons.ExternalLink className="h-3 w-3" aria-hidden="true" />
              ) : undefined
            }
          />
        );
      })}
    </PocketSection>
  );
}
